import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import MeetingsTab from "./meetings/MeetingsTab";

export default function Meetings() {
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite("meetings") : true;

  const [meetings, setMeetings] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [m, f] = await Promise.all([api.get("/meetings"), api.get("/financial-years")]);
      setMeetings(m.data);
      setFinancialYears(f.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="meetings-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
            Governance
          </div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Board Meetings</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Central repository for all Executive Board (quarterly) and General Board (annual)
            meetings — capture agenda, resolutions, attendance and supporting documents.
          </p>
        </div>
      </div>

      {!writable && (
        <div
          data-testid="meetings-readonly-banner"
          className="mb-6 px-4 py-3 border border-[#d4af37]/40 bg-[#d4af37]/5 rounded-sm text-sm text-[#8a6d1d]"
        >
          You have read-only access to Board Meetings.
        </div>
      )}

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : financialYears.length === 0 ? (
        <div
          data-testid="meetings-no-fy"
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center"
        >
          <div className="font-serif text-2xl text-[#1c1a17]">No Financial Years configured</div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Add a Financial Year under Trust Details → Financial Years before recording meetings.
          </div>
        </div>
      ) : (
        <Tabs defaultValue="executive">
          <TabsList className="bg-transparent p-0 border-b border-[#e5e0d8] rounded-none h-auto w-full justify-start gap-4 flex-wrap mb-6">
            <TabsTrigger
              value="executive"
              data-testid="tab-executive"
              className="data-[state=active]:bg-transparent data-[state=active]:text-[#234333] data-[state=active]:border-b-2 data-[state=active]:border-[#234333] rounded-none px-0 py-3 text-sm font-sans text-[#6b655b] shadow-none"
            >
              Executive Board (Quarterly)
            </TabsTrigger>
            <TabsTrigger
              value="general"
              data-testid="tab-general"
              className="data-[state=active]:bg-transparent data-[state=active]:text-[#234333] data-[state=active]:border-b-2 data-[state=active]:border-[#234333] rounded-none px-0 py-3 text-sm font-sans text-[#6b655b] shadow-none"
            >
              General Board (Annual)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="executive">
            <MeetingsTab
              boardType="executive"
              meetings={meetings}
              financialYears={financialYears}
              refresh={refresh}
              writable={writable}
            />
          </TabsContent>
          <TabsContent value="general">
            <MeetingsTab
              boardType="general"
              meetings={meetings}
              financialYears={financialYears}
              refresh={refresh}
              writable={writable}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
